<!-- Outer Wrapper -->
<div class="absolute mt-4 right-0 p-4 w-[420px] bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700
               rounded-md shadow-lg max-h-120 overflow-y-auto z-30" #notificationsMenu #scrollContainer (scroll)="onScrollContainer($event)">

  <!-- Header -->
  <div class="flex items-center flex-wrap gap-3 mb-3">
    <h3 class="text-left text-base font-semibold text-zinc-800 dark:text-zinc-100 flex-1 m-0" id="notifications-heading"
      tabindex="-1">
      Notifications
    </h3>

    <!-- for filtering notifications active button make primary and inactive outlined secondary button -->
    <div class="flex items-center gap-2">
      <!-- Mute/Unmute Button -->
      <p-button
        [icon]="isSoundMuted() ? 'ti ti-volume-off' : 'ti ti-volume'"
        [severity]="isSoundMuted() ? 'secondary' : 'primary'"
        [outlined]="isSoundMuted()"
        size="small"
        rounded="true"
        (click)="toggleSoundMute()"
        (keydown.enter)="toggleSoundMute()"
        [attr.aria-label]="isSoundMuted() ? 'Unmute notifications' : 'Mute notifications'"
        pTooltip="Toggle sound"
        tooltipPosition="top">
        <span class="sr-only" aria-live="polite">{{this.isSoundMuted() ? 'Notification sound muted' : 'Notification sound unmuted'}}</span>
      </p-button>

      <div class="flex items-center gap-2">
        <p-button size="small" label="All" [severity]="showUnread() ? 'secondary' : 'primary'" [outlined]="showUnread()"
          rounded="true" (onClick)="onToggleChange(false)" (keydown.enter)="onToggleChange(false)"
          [attr.aria-pressed]="!showUnread()" [attr.aria-label]="'Show all notifications'"></p-button>

        <p-button size="small" label="Unread" [severity]="showUnread() ? 'primary' : 'secondary'"
          [outlined]="!showUnread()" rounded="true" (onClick)="onToggleChange(true)" (keydown.enter)="onToggleChange(true)"
          [attr.aria-pressed]="showUnread()" [attr.aria-label]="'Show unread notifications only'"></p-button>
      </div>
      <div class="relative">
        <p-button #menuBtn
          icon="ti ti-dots-vertical"
          [severity]="!isMenuOpened() ? 'secondary' : 'primary'"
          [outlined]="!isMenuOpened()"
          size="small"
          rounded="true"
          (onClick)="toggleMenu()"
          [attr.aria-label]="isMenuOpened() ? 'Close more menu' : 'Open more menu'"
          pTooltip="Toggle sound"
          tooltipPosition="top">
          <span class="sr-only" aria-live="polite">{{this.isMenuOpened() ? 'More Menu opened' : 'More Menu closed'}}</span>
        </p-button>
        <div *ngIf="isMenuOpened()"
          class="absolute right-0 m-2 !w-[150px] rounded-lg border border-zinc-300
                  p-2 w-fit bg-white dark:bg-black shadow-lg z-[9999]">

          <button (click)="markAllAsRead()" 
          class="flex items-center !w-full gap-2 p-2 w-full text-left text-sm rounded-md
                  hover:bg-zinc-100 dark:hover:bg-zinc-800 mb-1 last:mb-0">

          <i class="ti ti-eye"></i>
            Mark All As Read
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Notification Skeleton -->
  <div class="p-3 space-y-6 animate-pulse" *ngIf="isLoading()">
    <ng-container *ngFor="let i of [1,2,3]">
      <!-- Group Label -->
      <p-skeleton width="120px" height="16px" class="ml-1"></p-skeleton>
      <!-- Notification Items -->
      <div class="space-y-2 mt-3">
        <ng-container *ngFor="let j of [1,2,3]">
          <div class="flex items-start gap-3 px-3 py-3 rounded-lg border border-transparent">
            <!-- Icon -->
            <p-skeleton shape="circle" size="40px"></p-skeleton>
            <!-- Text Content -->
            <div class="flex-1 space-y-2">
              <p-skeleton width="50%" height="14px" class="block"></p-skeleton>

              <p-skeleton width="80%" height="12px" class="block"></p-skeleton>

              <p-skeleton width="30%" height="10px" class="mt-1 block"></p-skeleton>
            </div>
          </div>
        </ng-container>
      </div>
    </ng-container>
  </div>

  <!-- Notification List with Lazy Loading -->
  <div *ngIf="!isLoading()">
    <ng-container *ngIf="notifications().length; else emptyState">
      <div *ngFor="let group of groupedNotifications(); trackBy: trackByGroup"
        class="first:border-t-0 border-t border-zinc-200 dark:border-zinc-700 first:pt-0 pt-3 mb-3">

        <h4 class="flex text-xs uppercase font-semibold mb-2 text-zinc-500 dark:text-zinc-400 align-left"> {{ group.label }}<span class="sr-only">Notifications</span> </h4>

        <div *ngFor="let n of group.items; trackBy: trackByNotificationId" (click)="handleNotificationClick(n)"
          (keydown.enter)="handleNotificationClick(n)"
          class="flex items-start gap-3 px-3 py-3 relative group cursor-pointer rounded-lg transition-colors mb-2"
          [ngClass]="{
                'bg-brand-50 dark:bg-brand-700/50 hover:bg-brand-100 dark:hover:bg-brand-700/70 text-zinc-700 dark:text-zinc-300': !n.is_read,
                'bg-transparent hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400': n.is_read
              }">

          <!-- Icon -->
          <div
            class="flex-shrink-0 h-10 w-10 rounded-md flex items-center justify-center overflow-hidden bg-zinc-400/20">
            <img *ngIf="n.actor_image_url; else fallbackIcon" [src]="n.actor_image_url" alt="Actor"
              class="h-full w-full object-cover" />

            <ng-template #fallbackIcon>
              <i class="ti ti-user-filled text-2xl text-zinc-400" aria-hidden="true"></i>
            </ng-template>
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <h5 class="flex text-left text-sm font-medium  leading-tight" [ngClass]="{
                'text-brand-500 dark:text-brand-300': !n.is_read,
                'text-zinc-800 dark:text-zinc-100': n.is_read
              }">
              <span class="sr-only">{{!n.is_read ? 'Unread notification' : ''}}</span>{{ n.notification_type.replaceAll('_', ' ') }}
            </h5>
            <p class="text-left text-xs leading-snug">
              <span class="sr-only">Description </span>{{ n.message }}
            </p>
            <span class="flex text-xs text-zinc-400 dark:text-zinc-500 mt-1 block"><span class="sr-only">Received on </span>{{ n.created_ts | date }}</span>
          </div>
        </div>
      </div>

      <!-- Loading indicator for lazy load -->
      <div *ngIf="isLoadingMore()" class="flex items-center justify-center py-4">
        <div class="flex flex-col items-center gap-2">
          <i class="ti ti-loader text-lg text-zinc-400 animate-spin" aria-hidden="true"></i>
          <span class="text-xs text-zinc-500">Loading more...</span>
        </div>
      </div>

      <!-- End of list indicator -->
      <div *ngIf="hasLoadedAll() && notifications().length > 0" class="text-center py-3">
        <span class="text-xs text-zinc-400 dark:text-zinc-500">No more notifications</span>
      </div>
    </ng-container>

    <!-- Empty State -->
    <ng-template #emptyState>
      <div class="px-4 py-12 text-center text-zinc-500 dark:text-zinc-400">
        <i class="ti ti-bell-off text-3xl mb-2 block"></i>
        <p class="text-sm">You're all caught up!</p>
      </div>
    </ng-template>
  </div>

  <!-- Notification Sound -->
  <audio #notificationSound preload="auto">
    <source src="assets/sounds/level-up-191997.mp3" type="audio/mpeg" />
  </audio>
</div>